import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getEmployerJobs, updateJobStatus, deleteJob, JOB_STATUS } from '../services/jobService';
import { getEmployerApplications } from '../services/applicationService';
import JobCard from '../components/JobCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const ManageJobs = ({ user, userData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user || userData?.userType !== 'employer') {
      navigate('/dashboard');
      return;
    }
    
    // Check if filter was passed from dashboard
    if (location.state?.filter) {
      console.log('📌 [ManageJobs] Filter received from navigation:', location.state.filter);
      setStatusFilter(location.state.filter);
    }
    
    // Check if message was passed from PostJob
    if (location.state?.message) {
      console.log('📌 [ManageJobs] Message received from navigation:', location.state.message);
      setMessage(location.state.message);
      // Clear message after 5 seconds
      setTimeout(() => setMessage(''), 5000);
    }
    
    fetchJobs();
  }, [user, userData, navigate, location.state]);

  const ownsJob = (job) => {
    return [job.userId, job.employerId, job.companyId].filter(Boolean).includes(user?.uid);
  };

  const filteredJobs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return jobs.filter((job) => {
      if (statusFilter !== 'all' && job.status !== statusFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      return [
        job.title,
        job.department,
        job.location?.city,
        job.location?.state,
        job.company
      ].some(value => value?.toLowerCase().includes(normalizedSearch));
    });
  }, [jobs, statusFilter, searchTerm]);

  const fetchJobs = async () => {
    console.log('🔄 [ManageJobs] Fetching jobs and applications for user:', user.uid);
    setLoading(true);
    try {
      // Fetch jobs and applications in parallel
      const [jobsResult, applicationsResult] = await Promise.all([
        getEmployerJobs(user.uid, { limit: 100 }),
        getEmployerApplications(user.uid)
      ]);
      
      console.log('📊 [ManageJobs] Jobs result:', jobsResult);
      console.log('📊 [ManageJobs] Applications result:', applicationsResult);
      
      if (jobsResult.success) {
        // Keep this compatible with older job docs that used employerId/companyId.
        const userJobs = jobsResult.jobs.filter(ownsJob);
        console.log('✅ [ManageJobs] User jobs count:', userJobs.length);
        setJobs(userJobs);
      } else {
        console.error('❌ [ManageJobs] Failed to fetch jobs:', jobsResult.error);
        setJobs([]);
      }

      if (applicationsResult.success) {
        console.log('✅ [ManageJobs] Applications count:', applicationsResult.applications.length);
        setApplications(applicationsResult.applications);
      } else {
        console.error('❌ [ManageJobs] Failed to fetch applications:', applicationsResult.error);
        setApplications([]);
      }
    } catch (error) {
      console.error('❌ [ManageJobs] Error fetching data:', error);
      setJobs([]);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    console.log('🔄 [ManageJobs] Updating job status:', jobId, newStatus);
    
    // Verify job ownership before updating
    const job = jobs.find(j => j.id === jobId);
    if (!job || !ownsJob(job)) {
      alert('Error: You do not have permission to update this job.');
      console.error('❌ [ManageJobs] Unauthorized status update attempt');
      return;
    }
    
    try {
      const result = await updateJobStatus(jobId, newStatus, user.uid);
      if (result.success) {
        setJobs(jobs.map(job =>
          job.id === jobId ? { ...job, status: newStatus } : job
        ));
        console.log('✅ [ManageJobs] Job status updated successfully');
      } else {
        alert('Error updating job status: ' + result.error);
        console.error('❌ [ManageJobs] Status update failed:', result.error);
      }
    } catch (error) {
      alert('Error updating job status: ' + error.message);
      console.error('❌ [ManageJobs] Status update error:', error);
    }
  };

  const handleDeleteJob = async (jobId) => {
    console.log('🗑️ [ManageJobs] Attempting to delete job:', jobId);
    
    // Verify job ownership before deleting
    const job = jobs.find(j => j.id === jobId);
    if (!job || !ownsJob(job)) {
      setMessage('Error: You do not have permission to delete this job.');
      console.error('❌ [ManageJobs] Unauthorized delete attempt');
      return;
    }
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${job.title}"? This action cannot be undone.`)) {
      console.log('ℹ️ [ManageJobs] Job deletion cancelled by user');
      return;
    }
    
    try {
      const result = await deleteJob(jobId, user.uid);
      if (result.success) {
        setJobs(jobs.filter(job => job.id !== jobId));
        setMessage('Job deleted successfully!');
        console.log('✅ [ManageJobs] Job deleted successfully');
        // Clear message after 3 seconds
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error deleting job: ' + result.error);
        console.error('❌ [ManageJobs] Delete failed:', result.error);
      }
    } catch (error) {
      setMessage('Error deleting job: ' + error.message);
      console.error('❌ [ManageJobs] Delete error:', error);
    }
  };

  const handleEditJob = (jobId) => {
    console.log('✏️ [ManageJobs] Editing job:', jobId);
    const job = jobs.find(j => j.id === jobId);
    if (!job || !ownsJob(job)) {
      setMessage('Error: You do not have permission to edit this job.');
      return;
    }
    
    // Navigate to post-job page with job data for editing
    navigate('/post-job', { 
      state: { 
        editMode: true, 
        jobData: job,
        jobId: jobId
      } 
    });
  };

  const handleViewApplications = (jobId) => {
    console.log('👀 [ManageJobs] Viewing applications for job:', jobId);
    const job = jobs.find(j => j.id === jobId);
    if (!job) {
      setMessage('Job not found.');
      return;
    }
    
    // Navigate to applications page with job filter
    navigate('/applications', { 
      state: { 
        jobFilter: jobId,
        jobTitle: job.title
      } 
    });
  };

  const getStatusCounts = () => {
    const counts = {
      all: jobs.length,
      active: jobs.filter(job => job.status === JOB_STATUS.ACTIVE).length,
      draft: jobs.filter(job => job.status === JOB_STATUS.DRAFT).length,
      paused: jobs.filter(job => job.status === JOB_STATUS.PAUSED).length,
      closed: jobs.filter(job => job.status === JOB_STATUS.CLOSED).length,
      expired: jobs.filter(job => job.status === JOB_STATUS.EXPIRED).length
    };
    return counts;
  };

  const getJobApplicationCount = (jobId) => {
    return applications.filter(app => app.jobId === jobId).length;
  };

  const handleStatusCardClick = (status) => {
    console.log('📊 [ManageJobs] Status card clicked:', status);
    setStatusFilter(status);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const statusCounts = getStatusCounts();

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Manage Jobs
              </h1>
              <p className="mt-2 text-gray-600 text-lg">
                View and manage your job postings in one place
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/dashboard"
                className="text-indigo-600 hover:text-indigo-800 font-medium text-sm flex items-center gap-1 px-4 py-2 border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </Link>
              <Link
                to="/post-job"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-2 rounded-xl font-semibold flex items-center gap-2 shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Post New Job
              </Link>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-xl ${
            message.includes('Error') || message.includes('error') 
              ? 'bg-red-50 border border-red-200 text-red-800' 
              : 'bg-green-50 border border-green-200 text-green-800'
          }`}>
            <div className="flex items-center gap-2">
              {message.includes('Error') || message.includes('error') ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {message}
            </div>
          </div>
        )}

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <button
            onClick={() => handleStatusCardClick('all')}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === 'all' ? 'ring-2 ring-indigo-500 bg-indigo-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Jobs</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{statusCounts.all}</p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-3">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleStatusCardClick(JOB_STATUS.ACTIVE)}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === JOB_STATUS.ACTIVE ? 'ring-2 ring-green-500 bg-green-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Active Jobs</p>
                <p className="text-3xl font-bold text-green-600 mt-2">{statusCounts.active}</p>
              </div>
              <div className="bg-green-50 rounded-2xl p-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleStatusCardClick(JOB_STATUS.DRAFT)}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === JOB_STATUS.DRAFT ? 'ring-2 ring-yellow-500 bg-yellow-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Draft Jobs</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{statusCounts.draft}</p>
              </div>
              <div className="bg-yellow-50 rounded-2xl p-3">
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleStatusCardClick(JOB_STATUS.PAUSED)}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === JOB_STATUS.PAUSED ? 'ring-2 ring-orange-500 bg-orange-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Paused Jobs</p>
                <p className="text-3xl font-bold text-orange-600 mt-2">{statusCounts.paused}</p>
              </div>
              <div className="bg-orange-50 rounded-2xl p-3">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleStatusCardClick(JOB_STATUS.CLOSED)}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === JOB_STATUS.CLOSED ? 'ring-2 ring-red-500 bg-red-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Closed Jobs</p>
                <p className="text-3xl font-bold text-red-600 mt-2">{statusCounts.closed}</p>
              </div>
              <div className="bg-red-50 rounded-2xl p-3">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </button>

          <button
            onClick={() => handleStatusCardClick(JOB_STATUS.EXPIRED)}
            className={`bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
              statusFilter === JOB_STATUS.EXPIRED ? 'ring-2 ring-gray-500 bg-gray-50' : ''
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Expired Jobs</p>
                <p className="text-3xl font-bold text-gray-500 mt-2">{statusCounts.expired}</p>
              </div>
              <div className="bg-gray-100 rounded-2xl p-3">
                <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </button>
        </div>

        {/* Enhanced Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Search Jobs
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search by title, department, or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="lg:w-64">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="all">All Status ({statusCounts.all})</option>
                <option value={JOB_STATUS.ACTIVE}>Active ({statusCounts.active})</option>
                <option value={JOB_STATUS.DRAFT}>Draft ({statusCounts.draft})</option>
                <option value={JOB_STATUS.PAUSED}>Paused ({statusCounts.paused})</option>
                <option value={JOB_STATUS.CLOSED}>Closed ({statusCounts.closed})</option>
                <option value={JOB_STATUS.EXPIRED}>Expired ({statusCounts.expired})</option>
              </select>
            </div>
            {(searchTerm || statusFilter !== 'all') && (
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setStatusFilter('all');
                    setSearchTerm('');
                  }}
                  className="px-4 py-3 text-indigo-600 hover:text-indigo-800 font-medium text-sm border border-indigo-200 rounded-xl hover:bg-indigo-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Jobs List */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Your Job Postings</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {filteredJobs.length} of {jobs.length} jobs
                  {statusFilter !== 'all' && ` • Filtered by: ${statusFilter}`}
                  {searchTerm && ` • Search: "${searchTerm}"`}
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {filteredJobs.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {jobs.length === 0 ? 'No jobs posted yet' : 'No jobs match your filters'}
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  {jobs.length === 0 
                    ? 'Create your first job posting to start attracting top talent and grow your team.'
                    : 'Try adjusting your search or filter criteria to find the jobs you\'re looking for.'
                  }
                </p>
                {jobs.length === 0 ? (
                  <Link
                    to="/post-job"
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold inline-flex items-center gap-2 transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Your First Job
                  </Link>
                ) : (
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setSearchTerm('');
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold"
                  >
                    Clear All Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {filteredJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    showActions={true}
                    onStatusChange={handleStatusChange}
                    onDelete={handleDeleteJob}
                    onEdit={handleEditJob}
                    onViewApplications={handleViewApplications}
                    applicationCount={getJobApplicationCount(job.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Footer */}
    <Footer />
  </>
  );
};

export default ManageJobs;
